import React, { useEffect, useState } from 'react';

import styled, { css } from 'styled-components';

import CtaButton from 'components/CtaButton/CtaButton';
import { AlertSize } from 'components/Layout/Header/components/AlertBanner';
import SidebarItem from 'components/Layout/Header/components/Sidebar/SidebarItem';

import { SubMenuItemI } from 'interfaces/content-stack/contentStack.types';

import { getModularBlockLink } from 'utils/modularBlockUtils';

const SidebarContainer = styled.div<{
  $isOpen: boolean;
  $isMounted: boolean;
  $alertOpen: boolean;
  $alertHeight: number;
}>`
  ${({ theme, $alertOpen, $isOpen, $isMounted, $alertHeight }) => css`
    display: none;
    padding: 1rem;
    position: fixed;
    top: ${$alertOpen ? `calc(${$alertHeight} + 3.8rem)` : '3.8rem'};
    left: 0;
    width: 90vw;
    height: calc(100% - 3.5rem);
    overflow: auto;
    background: white;
    transform: ${$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: ${$isMounted ? 'transform 0.3s ease-in-out' : 'none'};
    z-index: 11;
    overscroll-behavior: contain;

    @media (max-width: ${theme.breakpoints.lg}) {
      display: block;
    }

    a {
      margin-top: 1rem;
    }
  `}
`;

const Overlay = styled.div<{ $isOpen: boolean; $alertOpen: boolean; $alertHeight: number }>`
  ${({ theme, $alertOpen, $isOpen, $alertHeight }) => css`
    display: none;
    width: 100vw;
    background-color: rgba(29, 25, 27, 0.65);
    position: fixed;
    top: ${$alertOpen ? `calc(${$alertHeight} + 3.8rem)` : '3.8rem'};
    bottom: 0;
    left: 0;
    right: 0;
    transition: opacity 0.3s ease-in-out;
    opacity: 0;
    z-index: -1;

    @media (max-width: ${theme.breakpoints.lg}) {
      display: block;
    }

    ${$isOpen &&
    `
      opacity: 1;
      z-index: 10;`}
  `}
`;

const SidebarMenuItemContainer = styled.div<{ $alertOpen: boolean }>`
  width: 100%;
  margin-bottom: ${(props) => (props.$alertOpen ? `calc(${AlertSize.xs} + 1.5rem)` : '1.5rem')};
`;

interface SidebarProps {
  isOpen: boolean;
  menuItems?: SubMenuItemI[];
  setIsOpen: () => void;
  alertOpen: boolean;
  alertHeight: number;
  toggleSidebar: () => void;
}

const Sidebar = ({ isOpen, menuItems, setIsOpen, alertOpen, alertHeight, toggleSidebar }: SidebarProps) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <Overlay onClick={() => setIsOpen()} $isOpen={isOpen} $alertOpen={alertOpen} $alertHeight={alertHeight / 16} />
      <SidebarContainer $isOpen={isOpen} $isMounted={isMounted} $alertOpen={alertOpen} $alertHeight={alertHeight / 16}>
        <SidebarMenuItemContainer $alertOpen={alertOpen} data-testid='sidebar'>
          {menuItems?.map((item: SubMenuItemI, index: number) => {
            return (
              <>
                {item.specialBehavior === 'open_an_account' ? (
                  <CtaButton
                    buttonType='primary'
                    openInNewWindow={item.link?.[0]?.openInNewWindow ?? false}
                    text={item.linkText}
                    to={getModularBlockLink(item.link) ?? ''}
                    data-testid='mobile-open-account-button'
                  />
                ) : (
                  <SidebarItem key={index} data={item} toggleSidebar={toggleSidebar} />
                )}
              </>
            );
          })}
        </SidebarMenuItemContainer>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
